/* eslint-disable react/no-danger */

import React, { useEffect, useContext, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { connect } from 'react-redux';
import {
  PrivacySettingToggle,
  PrivacySettingToggles,
} from '@prisjakt/web-app-config/types/market';
import T from '@prisjakt/ui-translate';
import { AppContext } from '@prisjakt/ui-app-context';
import { NudgeDown } from '@prisjakt/ui/hideable/variants';
import { Speed } from '@prisjakt/ui';
import { ViewportContext } from '../viewport';
import CtxLink from '../render-context/ctx-link';
import { pushEvent } from '../../client/data-layer';
import acceptCookiesAction from './actions';
import { AppState } from '../../reducers';

import * as S from './styles';

export const SSRCookieBannerMobileId = 'root-cookie-banner-mobile';
export const SSRCookieBannerDesktopId = 'root-cookie-banner-desktop';

type OwnProps = {};

type StateProps = {
  version: string;
  settings: PrivacySettingToggle;
  isLoggedIn: boolean;
  isActive: boolean;
};

type DispatchProps = {
  acceptCookies: typeof acceptCookiesAction;
};

type Props = OwnProps & StateProps & DispatchProps;

const containerProps = {
  id: 'cookie-banner',
  'data-test': 'CookieBanner',
};

const Logo = () => {
  const { market } = useContext(AppContext);
  const cdn = market && market.cdn ? market.cdn : 'https://cdn.pji.nu';
  return (
    <S.LogoWrapper>
      <S.Logo>
        <img
          alt="Schibsted"
          src={`${cdn}/g/rfe/logos/schibsted/schibsted_next_white.png`}
          width="116"
        />
      </S.Logo>
    </S.LogoWrapper>
  );
};

type CookieBannerComponentProps = {
  isVisible: boolean;
  isDesktop: boolean;
  onAcceptCookies: () => void;
};

/*
🚨🚨🚨🚨 HACKILLY HACK STUFF GOING ON HERE 🚨🚨🚨🚨
To improve our LCP we server-side two version of this component (desktop/mobile).
Then use some pure js in the index.html to toggle it on if necessary. Before react
has been loaded and removing the servers side banners and taking over the rendering.
So keep this component purely visual and all toggle on and off and accept logic in the 
CookieBannerHOC component
*/
export const CookieBannerComponent = ({
  isVisible,
  isDesktop,
  onAcceptCookies,
}: CookieBannerComponentProps) => {
  const { t, renderMode, market } = useContext(AppContext);

  const privacySettings =
    market &&
    market.privacySettings &&
    (market.privacySettings[renderMode] || market.privacySettings.responsive);
  const cookiePolicyLink = useMemo(
    () => (
      <CtxLink to={privacySettings.cookieUrl} key="cookie">
        {t('common', 'cookie-banner.cookie-policy')}
      </CtxLink>
    ),
    [t, privacySettings.cookieUrl]
  );

  const privacyPolicyLink = useMemo(
    () => (
      <CtxLink to={privacySettings.privacyUrl} key="privacy">
        {t('common', 'cookie-banner.privacy-policy')}
      </CtxLink>
    ),
    [t, privacySettings.privacyUrl]
  );
  return (
    <NudgeDown
      visible={isVisible}
      container={S.Banner}
      containerProps={containerProps}
      speed={Speed.Slow}
    >
      <S.BannerContent>
        <S.BannerText>
          {!isDesktop && <Logo />}
          <T
            domain="common"
            cookiePolicyLink={cookiePolicyLink}
            privacyPolicyLink={privacyPolicyLink}
          >
            cookie-banner.description
          </T>
        </S.BannerText>

        <S.ButtonsWrapper>
          <S.AcceptButton color="primary" onClick={onAcceptCookies}>
            <T domain="common">cookie-banner.accept-button</T>
          </S.AcceptButton>
          <S.Button
            href={privacySettings.privacySettingsUrl}
            color="tertiary"
            variant="outlined"
          >
            {t('common', 'cookie-banner.privacy-policy-settings')}
          </S.Button>
        </S.ButtonsWrapper>

        {isDesktop && <Logo />}
      </S.BannerContent>
    </NudgeDown>
  );
};

const CookieBannerHOC = ({
  version,
  acceptCookies,
  settings,
  isLoggedIn,
  isActive,
}: Props) => {
  const { renderMode, market } = useContext(AppContext);
  const { isDesktop } = useContext(ViewportContext);
  const privacySettings =
    market &&
    market.privacySettings &&
    (market.privacySettings[renderMode] || market.privacySettings.responsive);
  const onAcceptCookies = useCallback(() => {
    acceptCookies(
      privacySettings.cookieVersion,
      {
        ...settings,
        [PrivacySettingToggles.BehaviorTrackingMarketing]: true,
      },
      isLoggedIn
    );
  }, [isLoggedIn, privacySettings, acceptCookies, settings]);

  const removeServerSideCookieBanner = (id: string) => {
    const element = document?.getElementById(id);
    if (!element) return;
    element.innerHTML = '';
  };
  useEffect(() => {
    const visible = !window.SESSION_DATA.session.cookie_consent_version;
    pushEvent('cookieBannerIsVisible', { visible });
    removeServerSideCookieBanner(SSRCookieBannerMobileId);
    removeServerSideCookieBanner(SSRCookieBannerDesktopId);
  }, []);

  const isVisible = version !== privacySettings.cookieVersion;

  if (!isActive) {
    return null;
  }

  if (renderMode === 'native') {
    return null;
  }

  const rootElement = document?.getElementById('root-cookiebanner');

  if (!rootElement) {
    return null;
  }

  return createPortal(
    <CookieBannerComponent
      isVisible={isVisible}
      isDesktop={!!isDesktop}
      onAcceptCookies={onAcceptCookies}
    />,
    rootElement
  );
};

const mapStateToProps = (state: AppState) => ({
  version: state.globals.cookieBanner.version,
  isActive: state.globals.cookieBanner.isActive,
  settings: state.areas.user.tracking.settings,
  isLoggedIn: state.areas.account.isAuthenticated,
});

const dispatchProps = {
  acceptCookies: acceptCookiesAction,
};

export default connect(mapStateToProps, dispatchProps)(CookieBannerHOC);
